import { z } from 'zod';
/**
 * The Mini reporter's calls (COL-010, TLS-D1 to TLS-D7). Every call carries the reporter token as
 * `Authorization: Bearer <token>`; the token opens these routes and nothing else.
 */
/** TLS-D1: new lines of `~/talos/runs.jsonl`, each exactly as written. */
export const reporterRunsBodySchema = z.object({ lines: z.array(z.string().min(2).max(8_000)).min(1).max(2_000) });
/** What Pulse did with the lines: stored (new or already there), and any it couldn't read, by index. */
export const reporterRunsResponseSchema = z.object({
    stored: z.number(),
    rejected: z.array(z.object({ index: z.number(), reason: z.string() })),
});
/** TLS-D2: the Mini's state when it reported. */
export const reporterHealthBodySchema = z.object({
    checkedAt: z.string().datetime({ offset: true }),
    stopSwitchSet: z.boolean(),
    locks: z.array(z.object({ name: z.string().min(1), since: z.string().datetime({ offset: true }) })),
    docker: z.object({ running: z.boolean(), containers: z.number().int().min(0), startsAtSignIn: z.boolean() }),
    uptimeSeconds: z.number().min(0),
    load: z.number().min(0),
    routines: z.array(z.object({ routineId: z.string().min(1), cronSchedule: z.string().min(1) })),
});
/** TLS-D8: the Mini's usage, a sample every 5 minutes; samples that failed to send come with the next. */
export const usageSampleSchema = z.object({
    sampledAt: z.string().datetime({ offset: true }),
    /** CPU in use, as a percentage of all cores. */
    cpuPercent: z.number().min(0).max(100),
    cores: z.number().int().min(1),
    memoryUsedBytes: z.number().int().min(0),
    memoryTotalBytes: z.number().int().min(1),
    /** macOS's memory pressure level. */
    memoryPressure: z.enum(['normal', 'warn', 'critical']),
    swapUsedBytes: z.number().int().min(0),
    /** The runs in progress: the lock files in `~/talos/locks/`, by name. */
    running: z.array(z.string().min(1).max(80)).max(20),
});
export const reporterUsageBodySchema = z.object({ samples: z.array(usageSampleSchema).min(1).max(600) });
/** TLS-D7: Talos's written lines for a business date: up to three per member, one per product or PR. */
export const reporterLinesBodySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    members: z.record(z.array(z.string().min(1).max(400)).min(1).max(3)).default({}),
    products: z.record(z.string().min(1).max(400)).default({}),
    /** Keyed `<repository>#<number>`. */
    pullRequests: z.record(z.string().min(1).max(400)).default({}),
});
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const text = (max) => z.string().trim().max(max);
/** COL-011: what Talos reads before assigning, for every project: its Pulse milestones and its items. */
export const reporterProjectsResponseSchema = z.object({
    projects: z.array(z.object({
        slug: z.string(),
        name: z.string(),
        milestones: z.array(z.object({ id: z.string(), title: z.string(), description: z.string(), date: z.string(), state: z.string(), itemKeys: z.array(z.string()) })),
        items: z.array(z.object({ key: z.string(), ref: z.string(), title: z.string(), state: z.string(), done: z.boolean(), milestoneId: z.string().nullable() })),
    })),
});
/** COL-011 AC-1 (MS-3, MS-4): Talos's assignment for a project's milestones, with its line on each. */
export const reporterAssignmentsBodySchema = z.object({
    project: z.string().min(1),
    milestones: z.array(z.object({ milestoneId: z.string().min(1), itemKeys: z.array(z.string().min(1)).max(2_000), line: text(400).nullable().default(null) })).min(1),
});
/** COL-011 AC-2 (WR-1): the weekly report; `week` is any date in it. */
export const reporterReportBodySchema = z.object({
    week: date,
    team: text(2_000).min(1),
    projects: z.record(z.object({ moved: text(1_000), risk: text(1_000), next: text(1_000) })),
});
/** SHP-006 AC-1: which week or month Talos asks for; `period` is any date in it. */
export const reporterShippedQuerySchema = z.object({ view: z.enum(['week', 'month']).default('week'), period: date });
/** SHP-006 AC-1: a finished week's or month's delivered work per project, for Talos to write from. */
export const reporterShippedResponseSchema = z.object({
    view: z.enum(['week', 'month']),
    /** The week's Monday or the month's first day. */
    period: date,
    /** "week of 21 September", "August" */
    label: z.string(),
    projects: z.array(z.object({
        slug: z.string(),
        name: z.string(),
        toProduction: z.number().int(),
        toDev: z.number().int(),
        delivered: z.array(z.object({ ref: z.string(), title: z.string(), prs: z.number().int(), when: z.string().nullable() })),
        otherFixes: z.array(z.string()),
        housekeeping: z.number().int(),
        inDev: z.array(z.object({ ref: z.string(), title: z.string(), prs: z.number().int() })),
    })),
});
/** SHP-006 AC-2: Talos's narrative of a finished week or month; `period` is any date in it. */
export const reporterNarrativeBodySchema = z.object({
    view: z.enum(['week', 'month']),
    period: date,
    team: text(2_000).min(1),
    /** By project slug. */
    projects: z.record(text(2_000).min(1)).default({}),
});
/** COL-011 AC-3 (MT-3): the notes of followed meetings Talos hasn't read yet. */
export const reporterUnreadMeetingsResponseSchema = z.object({
    meetings: z.array(z.object({
        meetingId: z.string(),
        occurrenceId: z.string(),
        title: z.string(),
        kind: z.string(),
        startedAt: z.string(),
        /** The projects it covers, by slug, and each project's Pulse milestones for date changes. */
        projects: z.array(z.object({ slug: z.string(), milestones: z.array(z.object({ id: z.string(), title: z.string(), date: z.string() })) })),
        attendees: z.array(z.string()),
        summary: z.string(),
        nextSteps: z.array(z.string()),
    })),
});
/** COL-011 AC-3 (MT-3): Talos's reading of one occurrence, for each project the meeting covers. */
export const reporterReadingBodySchema = z.object({
    meetingId: z.string().min(1),
    occurrenceId: z.string().min(1),
    projects: z.record(z.object({
        /** What the notes said about this project. */
        summary: text(2_000),
        nextSteps: z.array(text(400).min(1)).max(20).default([]),
        /** What matters for PM: decisions and actions tied to milestones. */
        talos: text(1_000).default(''),
        /** A milestone date change Talos heard agreed; applied only when a member accepts it (MT-3). */
        change: z.object({ milestoneId: z.string().min(1), to: date }).nullable().default(null),
        /** MTG-004: milestones the meeting agreed, created unless the project already has one of that title. */
        newMilestones: z.array(z.object({ title: text(200).min(1), description: text(2_000).default(''), date })).max(10).optional(),
    })),
});
//# sourceMappingURL=reporter.js.map